import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Wand2, Check, Lightbulb, ArrowRight, Zap, MessageCircle, TrendingUp, BookOpen, Flame } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSubscription } from "@/context/SubscriptionContext";

/* ── Hook type detector ── */
const HOOK_TYPES = [
  { label: "Question",   icon: MessageCircle, color: "#3B82F6", bg: "#EFF6FF", match: /\?$/ },
  { label: "Contrarian", icon: Zap,           color: "#E8522A", bg: "#FFF4F0", match: /but|wrong|myth|stop|actually|nobody|don't/i },
  { label: "Trending",   icon: TrendingUp,    color: "#10B981", bg: "#ECFDF5", match: /\d+%|\$[\d,]+|million|billion|viral|fastest/i },
  { label: "Story",      icon: BookOpen,      color: "#8B5CF6", bg: "#F5F3FF", match: /^I |^When I|^Last year|^My /i },
  { label: "Bold",       icon: Flame,         color: "#F59E0B", bg: "#FFFBEB", match: /.*/ }, // default
];

function detectHookType(hook) {
  return HOOK_TYPES.find(t => t.match.test(hook)) || HOOK_TYPES[HOOK_TYPES.length - 1];
}

/* ── Hook cards visual display ── */
function HookCards({ hooks, ideaTitle, selectedIndex = 0, onSelectHook }) {
  const [active, setActive] = useState(selectedIndex);

  useEffect(() => {
    setActive(selectedIndex);
  }, [selectedIndex]);

  const selectHook = (idx) => {
    setActive(idx);
    if (onSelectHook) {
      onSelectHook(idx, hooks[idx]);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-border bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Opening Hooks · {hooks.length} options
        </p>
        <div className="flex items-center gap-1">
          {hooks.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectHook(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-4 bg-accent" : "w-1.5 bg-border hover:bg-muted-foreground"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Active hook card */}
      {hooks[active] && (() => {
        const type = detectHookType(hooks[active]);
        const Icon = type.icon;
        const cleanText = hooks[active].replace(/^["']|["']$/g, "").trim();
        return (
          <div className="p-4">
            {/* Type badge */}
            <div
              className="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
              style={{ background: type.bg, color: type.color }}
            >
              <Icon className="h-3 w-3" />
              {type.label} hook
            </div>

            {/* The hook text — big and prominent */}
            <p className="break-words font-display text-base font-bold leading-snug text-foreground sm:text-lg">
              "{cleanText}"
            </p>

            {/* Word count indicator */}
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">
                {cleanText.split(/\s+/).filter(Boolean).length} words
                {cleanText.split(/\s+/).filter(Boolean).length <= 15 && (
                  <span className="ml-1.5 font-semibold text-green-600">· Perfect length ✓</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => selectHook((active - 1 + hooks.length) % hooks.length)}
                  className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  onClick={() => selectHook((active + 1) % hooks.length)}
                  className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* All hooks quick-list */}
      <div className="border-t border-border bg-elevated/40">
        {hooks.map((h, i) => {
          const type = detectHookType(h);
          const Icon = type.icon;
          return (
            <button
              key={i}
              type="button"
              onClick={() => selectHook(i)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-elevated ${
                i === active ? "bg-elevated" : ""
              }`}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ background: type.bg }}
              >
                <Icon className="h-3 w-3" style={{ color: type.color }} />
              </span>
              <span
                className={`min-w-0 flex-1 text-xs ${
                  i === active ? "font-semibold text-foreground" : "text-muted-foreground"
                }`}
                style={{ wordBreak: "break-word" }}
              >
                {h}
              </span>
              {i === active && <span className="text-[10px] font-bold text-accent">Active</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Ideas() {
  const [state, setState] = useState({ loading: true, error: null });
  const [ideas, setIdeas] = useState([]);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [hookBusy, setHookBusy] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const opportunityId = searchParams.get("opportunity");
  const [opportunity, setOpportunity] = useState(null);
  const [contentByIdea, setContentByIdea] = useState({});
  const [selectedHooks, setSelectedHooks] = useState({});
  const [creatingContentId, setCreatingContentId] = useState(null);

  const { subscription, isTrial, isAdmin, openPaywall, refreshSubscription } = useSubscription();

  const ideasLimit = subscription?.quotas?.ideas || (isTrial ? 5 : 0);
  const ideasUsed = subscription?.usage?.ideas || 0;
  const ideasRemaining = Math.max(0, ideasLimit - ideasUsed);
  const canGenerateIdeas = isAdmin || ideasRemaining > 0;

  const hooksLimit = subscription?.quotas?.hooks || (isTrial ? 5 : 0);
  const hooksUsed = subscription?.usage?.hooks || 0;
  const hooksRemaining = Math.max(0, hooksLimit - hooksUsed);
  const canGenerateHooks = isAdmin || hooksRemaining > 0;

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [ideasRes, contentRes] = await Promise.all([
        api.get("/ideas"),
        api.get("/content").catch(() => ({ data: { content: [] } })),
      ]);
      setIdeas(ideasRes.data.ideas || []);
      const map = {};
      for (const c of contentRes.data?.content || []) {
        const item = { id: c.id, body: c.body || "" };
        if (c.idea_id) map[c.idea_id] = item;
        if (c.title) map[c.title.trim().toLowerCase()] = item;
      }
      setContentByIdea(map);
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (opportunityId)
      api.get(`/opportunities/${opportunityId}`)
        .then(({ data }) => setOpportunity(data))
        .catch(() => setOpportunity(null));
  }, [opportunityId]);

  const generate = async () => {
    if (!canGenerateIdeas) {
      openPaywall("AI Idea Generation");
      return;
    }
    if (!topic.trim() && !opportunityId) return toast.error("Enter a topic or focus first");
    setGenerating(true);
    try {
      const { data } = await api.post("/ideas/generate", {
        topic: topic.trim() || undefined,
        opportunity_id: opportunityId || undefined,
        count: 5,
      });
      setIdeas((prev) => [...data.ideas, ...prev]);
      toast.success(`Generated ${data.ideas.length} ideas`);
      setTopic("");
      refreshSubscription();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall("AI Idea Generation");
      }
      toast.error(apiErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const makeHooks = async (idea) => {
    if (!canGenerateHooks) {
      openPaywall("Hook Generation");
      return;
    }
    setHookBusy(idea.id);
    try {
      const { data } = await api.post(`/ideas/${idea.id}/hooks`, { count: 5 });
      setIdeas((prev) => prev.map((i) => (i.id === idea.id ? data.idea : i)));
      toast.success("Hooks generated");
      refreshSubscription();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall("Hook Generation");
      }
      toast.error(apiErrorMessage(err));
    } finally {
      setHookBusy(null);
    }
  };

  const select = async (idea) => {
    try {
      await api.post(`/ideas/${idea.id}/select`);
      setIdeas((prev) => prev.map((i) => (i.id === idea.id ? { ...i, selected: true } : i)));
      toast.success("Idea selected");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const createContent = async (idea) => {
    const existingEntry = contentByIdea[idea.id] || contentByIdea[idea.title?.trim()?.toLowerCase()];
    const existingId = typeof existingEntry === "object" ? existingEntry?.id : existingEntry;
    const existingBody = typeof existingEntry === "object" ? existingEntry?.body : "";

    const hookIdx = selectedHooks[idea.id] ?? 0;
    const chosenHook = (idea.hooks && idea.hooks.length > 0) ? (idea.hooks[hookIdx] || idea.hooks[0]) : null;
    const cleanHook = chosenHook ? chosenHook.replace(/^["']|["']$/g, "").trim() : "";
    const body = cleanHook ? `Hook:\n"${cleanHook}"\n\n` : "";

    if (existingId) {
      const isBlankOrUntouchedHook = !existingBody || !existingBody.trim() || (existingBody.trim().startsWith("Hook:\n") && existingBody.trim().length < 150);
      if (body && isBlankOrUntouchedHook) {
        try {
          await api.put(`/content/${existingId}`, { body });
          setContentByIdea((prev) => ({
            ...prev,
            [idea.id]: { id: existingId, body },
            [idea.title?.trim()?.toLowerCase()]: { id: existingId, body },
          }));
        } catch (e) {
          console.warn("Could not update existing draft body", e);
        }
      }
      toast.info("Opening draft for this idea");
      navigate(`/content/${existingId}`);
      return;
    }

    if (creatingContentId) return;
    setCreatingContentId(idea.id);
    try {
      const { data } = await api.post("/content", {
        title: idea.title,
        body,
        idea_id: idea.id,
        platforms: ["youtube", "instagram"],
      });
      setContentByIdea((prev) => ({
        ...prev,
        [idea.id]: { id: data.id, body: data.body || body },
        [idea.title?.trim()?.toLowerCase()]: { id: data.id, body: data.body || body },
      }));
      navigate(`/content/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setCreatingContentId(null);
    }
  };

  return (
    <AppShell title="Ideas">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Content Ideas</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Quota:</span>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-elevated border border-border text-foreground">
            {isAdmin ? "Unlimited" : `${ideasRemaining} left`}
          </span>
        </div>
      </div>


      {/* Opportunity context */}
      {opportunity && (
        <div className="mb-6 flex items-start gap-4 rounded-2xl border border-accent/30 bg-accent/5 p-5" data-testid="opportunity-context">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
            <Lightbulb className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-accent">Opportunity-backed ideas</p>
            <p className="mt-1 font-display text-base font-semibold text-foreground">{opportunity.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">Ideas will be generated from this opportunity's positioning and evidence.</p>
          </div>
        </div>
      )}

      {/* Generator bar */}
      <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5 shadow-sm sm:flex-row" data-testid="idea-generator">
        <Input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && generate()}
          placeholder={opportunityId ? "Optional refinement for this opportunity" : "Topic or focus, e.g. 'AI tools for solo founders'"}
          className="rounded-xl"
          data-testid="idea-topic"
        />
        <Button onClick={generate} disabled={generating} data-testid="idea-generate"
          className="shrink-0 rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm">
          <Sparkles className="mr-2 h-4 w-4" />
          {generating ? "Generating…" : "Generate ideas"}
        </Button>
      </div>

      {state.loading ? <Loader label="Loading ideas" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> :
       ideas.length === 0 ? (
        <EmptyState icon={Sparkles} testid="ideas-empty"
          title="No ideas yet"
          description="Enter a topic above and generate your first set of AI-powered content ideas." />
       ) : (
        <div className="grid gap-5 md:grid-cols-2" data-testid="ideas-list">
          {ideas.map((idea) => (
            <div key={idea.id}
              className="flex flex-col rounded-2xl border border-border bg-surface shadow-sm transition-all hover:border-accent/30 hover:shadow-md"
              data-testid="idea-card">

              {/* Card header */}
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-base font-bold text-foreground leading-snug">{idea.title}</h3>
                  {idea.selected && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-semibold text-accent">
                      <Check className="h-3 w-3" /> Selected
                    </span>
                  )}
                </div>

                {idea.format && (
                  <span className="mt-2 inline-block rounded-full border border-border bg-elevated px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {idea.format}
                  </span>
                )}

                {idea.description && (
                  <p className="mt-3 break-words text-sm leading-relaxed text-muted-foreground">{idea.description}</p>
                )}

                {idea.why_it_works && (
                  <div className="mt-3 rounded-xl border border-border bg-elevated p-3">
                    <p className="break-words text-xs leading-relaxed text-muted-foreground">
                      <span className="font-semibold text-foreground">Why it works: </span>
                      {idea.why_it_works}
                    </p>
                  </div>
                )}

                {/* Hook cards — visual representation */}
                {idea.hooks?.length > 0 && (
                  <HookCards
                    hooks={idea.hooks}
                    ideaTitle={idea.title}
                    selectedIndex={selectedHooks[idea.id] ?? 0}
                    onSelectHook={(idx) =>
                      setSelectedHooks((prev) => ({ ...prev, [idea.id]: idx }))
                    }
                  />
                )}
              </div>

              {/* Card footer actions */}
              <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-border px-4 sm:px-5 py-3">
                <Button size="sm" variant="outline" disabled={hookBusy === idea.id}
                  onClick={() => makeHooks(idea)}
                  className="rounded-full text-xs"
                  data-testid={`generate-hooks-${idea.id}`}>
                  <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                  {hookBusy === idea.id ? "Generating…" : idea.hooks?.length ? "New hooks" : "Generate hooks"}
                </Button>
                {!idea.selected && (
                  <Button size="sm" variant="ghost" onClick={() => select(idea)}
                    className="rounded-full text-xs"
                    data-testid={`select-idea-${idea.id}`}>
                    Select
                  </Button>
                )}
                {(() => {
                  const existingEntry = contentByIdea[idea.id] || contentByIdea[idea.title?.trim()?.toLowerCase()];
                  const existingId = typeof existingEntry === "object" ? existingEntry?.id : existingEntry;
                  return (
                    <Button
                      size="sm"
                      onClick={() => createContent(idea)}
                      disabled={creatingContentId === idea.id}
                      className={`ml-auto rounded-full text-xs ${
                        existingId
                          ? "border border-border bg-elevated text-foreground hover:bg-border/60"
                          : "bg-accent text-white hover:bg-accent-hover"
                      }`}
                      data-testid={`create-content-from-idea-${idea.id}`}
                    >
                      {existingId ? "Open Draft" : creatingContentId === idea.id ? "Opening…" : "Create Content"}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
       )}
    </AppShell>
  );
}
